// WP0 Task 2 — the ONE funnel event schema (spec §2: preview → signup →
// wizard steps → activation, instrumented from day one).
//
// WHERE events are recorded (locked here so later WPs don't invent pipes):
//   MARKETING_FUNNEL_EVENTS  → client-side via apps/marketing
//     src/lib/analytics.ts track() → POST /api/event → Analytics Engine
//     (the existing first-party sink; whitelist extended in WP0 Task 3).
//   DASHBOARD_FUNNEL_EVENTS  → server-side rows in the existing audit_log
//     D1 table (packages/db schema.ts, org_id+ts indexed, written by nothing
//     until WP0) via apps/dashboard src/lib/funnel.ts recordFunnelEvent()
//     (WP0 Task 4). WP7's console funnel table reads them by action prefix.
//
// BINDING AMENDMENT (Consolidation v2 §A1): server-side emissions (any
// worker) → audit_log via recordFunnelEvent — preview_started is emitted
// server-side on the draft-create path; only the two client-side island
// events ride the marketing AE sink.
//
// Leaf rules: `import type` only from glossary (erased by node strip-types);
// WIZARD_STEPS duplicates LAYERS at runtime with a lockstep test — the same
// pattern TRACKING.gridOptions / geo-grid-options.ts already use.
import { z } from 'zod';
import type { LayerKey } from './glossary.js';

export const WIZARD_STEPS = ['foundation', 'visibility', 'reputation', 'pulse'] as const satisfies readonly LayerKey[];

export const MARKETING_FUNNEL_EVENTS = ['preview_shown', 'signup_started'] as const;
export type MarketingFunnelEventName = (typeof MARKETING_FUNNEL_EVENTS)[number];

export const DASHBOARD_FUNNEL_EVENTS = [
  'preview_started', 'signup_completed', 'wizard_step_completed', 'wizard_completed', 'demo_claimed',
] as const;
export type DashboardFunnelEventName = (typeof DASHBOARD_FUNNEL_EVENTS)[number];

export const FUNNEL_EVENTS = [...MARKETING_FUNNEL_EVENTS, ...DASHBOARD_FUNNEL_EVENTS] as const;
export type FunnelEventName = (typeof FUNNEL_EVENTS)[number];

export interface FunnelEventPayloads {
  /** Visitor typed a business name (hero) / operator did (console, WP7). */
  preview_started: { source: 'hero' | 'console' };
  /** The real-data draft rendered. draftId is the server-side draft key (WP2). */
  preview_shown: { draftId: string; elapsedMs?: number };
  /** "make it yours" clicked — OAuth begins; draftId travels in OAuth state. */
  signup_started: { draftId?: string };
  /** Session + org exist. Fired once per new signup, server-side. */
  signup_completed: { provider: 'google' | 'apple' | 'magic_link'; draftId?: string };
  /** One wizard step finished OR skipped (skipped items land on the path). */
  wizard_step_completed: { step: LayerKey; skipped: boolean };
  /** THE activation metric (spec §2/§8): wizard finished end-to-end. */
  wizard_completed: { stepsSkipped: number };
  /** Cold-outreach demo claimed into an org (short-circuit door, spec §5). */
  demo_claimed: { demoSlug: string };
}
export type FunnelEventPayload<N extends FunnelEventName> = FunnelEventPayloads[N];

export const FUNNEL_EVENT_SCHEMAS = {
  preview_started: z.object({ source: z.enum(['hero', 'console']) }).strict(),
  preview_shown: z.object({
    draftId: z.string().min(1),
    elapsedMs: z.number().int().nonnegative().optional(),
  }).strict(),
  signup_started: z.object({ draftId: z.string().min(1).optional() }).strict(),
  signup_completed: z.object({
    provider: z.enum(['google', 'apple', 'magic_link']),
    draftId: z.string().min(1).optional(),
  }).strict(),
  wizard_step_completed: z.object({
    step: z.enum(WIZARD_STEPS),
    skipped: z.boolean(),
  }).strict(),
  wizard_completed: z.object({
    stepsSkipped: z.number().int().min(0).max(WIZARD_STEPS.length),
  }).strict(),
  demo_claimed: z.object({ demoSlug: z.string().min(1) }).strict(),
} as const satisfies Record<FunnelEventName, z.ZodType>;

/** audit_log.action prefix for dashboard-side funnel rows. WP7 queries
 *  `WHERE action LIKE 'funnel.%'` — never widen this to other row kinds. */
export const FUNNEL_AUDIT_PREFIX = 'funnel.' as const;

export interface FunnelAuditRow {
  action: string;
  metadataJson: Record<string, unknown>;
}

/** Pure half of the dashboard recorder (the fix-payload.ts pattern):
 *  validates the payload and shapes the audit_log columns. The impure half
 *  (ulid, clock, insert) is apps/dashboard/src/lib/funnel.ts. */
export function funnelAuditRow<N extends FunnelEventName>(
  name: N,
  payload: FunnelEventPayload<N>,
): FunnelAuditRow {
  const parsed: unknown = FUNNEL_EVENT_SCHEMAS[name].parse(payload);
  return {
    action: `${FUNNEL_AUDIT_PREFIX}${name}`,
    metadataJson: parsed as Record<string, unknown>,
  };
}
